import { useEffect } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { isBareShortcut } from '@/lib/hotkeys'

/** Mnemonic, and the only free letter that is. */
export const TODAY_KEY = 't'

/**
 * Back to Today from anywhere.
 *
 * Today is the screen the app is *for* — everything else is somewhere you went
 * to arrange something, and the way back was a click in the sidebar or, on a
 * phone, a tab. A single key is the difference between glancing at Insights
 * and going to look at Insights.
 *
 * Deliberately simpler than `InboxShortcut`, which also has to ask for focus:
 * there is no field to land in here, so navigating is the whole job. Kept as a
 * separate component rather than folded into a shared one, because two
 * near-identical things is where a premature abstraction usually starts.
 *
 * `preventDefault` because the key would otherwise be typed into whatever the
 * destination focuses — the trap `c`, `i` and `/` each fell into.
 */
export function TodayShortcut() {
  const navigate = useNavigate()
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!isBareShortcut(event, TODAY_KEY)) return
      event.preventDefault()
      // Already there: navigating again would push a pointless history entry
      // and make Back feel broken.
      if (pathname !== '/') void navigate({ to: '/' })
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [navigate, pathname])

  return null
}
